// src/services/driverQueueService.js
//
// Single source of truth for a fitter's ordered work queue. Replaces the
// per-event `driverSortOrder` field with one ordered array stored on
// `drivers/{uid}.queueOrder`. The array contains entries for BOTH:
//   - calendar events the fitter is assigned to (kind: 'event')
//   - jobs the fitter is direct-assigned to (kind: 'direct')
//
// The admin's DriverQueue page renders this unified list and writes the
// new array on drag-reorder. The fitter portal reads the same array to
// render the fitter's My Jobs list in the admin-controlled order.
//
// Reconciliation strategy:
//   - On each admin visit to DriverQueue, we re-read the live set of
//     assignments and merge them with the stored array. Stored entries
//     that no longer correspond to a real assignment are dropped.
//     New assignments (added since last reconcile) are appended to the
//     end so they're picked up automatically without a separate hook.
//   - If a job has BOTH a calendar event AND a direct assignment for
//     this fitter, only the event entry is kept (the event card already
//     represents the work; surfacing both would just clutter the list).
//
// Why reconciliation here and not via Cloud Functions: Yamen rejected
// Cloud Functions earlier (volume doesn't justify the operational cost).
// Reading-side reconciliation keeps the data model honest at near-zero
// cost — admins visit DriverQueue often enough that the stored array
// stays fresh.

import {
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { getCalendarsByDriver } from "./calendarService";
import { getJobsWithDirectDriver } from "./jobService";

const DRIVERS_COLLECTION = "drivers";

// Internal: stable key for an item so we can compare across reads.
const keyFor = (entry) => `${entry.kind}:${entry.id}`;

/**
 * Reconcile and return the fitter's queue.
 *
 * Returns:
 *   {
 *     driver,          // the drivers/{uid} doc (or null)
 *     items,           // ordered array of { kind, id, data } where data
 *                      // is the underlying event or job document
 *     queueOrder,      // the persisted ordering (kind+id only)
 *     events,          // raw calendar events for this fitter
 *     directJobs,      // raw direct-assigned jobs (with no event for
 *                      // this fitter) — already filtered before
 *                      // appearing in items
 *   }
 *
 * Writes back to Firestore only if reconciliation produced a different
 * ordering than what was stored.
 */
export const reconcileDriverQueue = async (driverId) => {
  if (!driverId) throw new Error("driverId is required");
  const ref = doc(db, DRIVERS_COLLECTION, driverId);

  // 1. Read what's stored alongside the live set of assignments.
  const [driverSnap, events, directJobs] = await Promise.all([
    getDoc(ref),
    getCalendarsByDriver(driverId),
    getJobsWithDirectDriver(driverId),
  ]);

  if (!driverSnap.exists()) {
    return { driver: null, items: [], queueOrder: [], events: [], directJobs: [] };
  }
  const driver = { id: driverSnap.id, ...driverSnap.data() };
  const stored = Array.isArray(driver.queueOrder) ? driver.queueOrder : [];

  // 2. Build the "should-be" set.
  //    - Every event for this fitter.
  //    - Direct-assigned jobs that have NO event for this fitter (the
  //      event card already represents them).
  const eventJobIds = new Set(events.map((e) => e.jobId).filter(Boolean));
  const liveEntries = [
    ...events.map((e) => ({ kind: "event", id: e.id })),
    ...directJobs
      .filter((j) => !eventJobIds.has(j.id))
      .map((j) => ({ kind: "direct", id: j.id })),
  ];
  const liveKeySet = new Set(liveEntries.map(keyFor));

  // 3. Reconcile: keep stored items still-live (preserving admin order),
  //    drop orphans, append anything new at the end in their natural
  //    fetched order.
  const finalOrder = [];
  const seenKeys = new Set();
  for (const entry of stored) {
    if (!entry || !entry.kind || !entry.id) continue;
    const key = keyFor(entry);
    if (liveKeySet.has(key) && !seenKeys.has(key)) {
      finalOrder.push({ kind: entry.kind, id: entry.id });
      seenKeys.add(key);
    }
  }
  for (const entry of liveEntries) {
    const key = keyFor(entry);
    if (!seenKeys.has(key)) {
      finalOrder.push(entry);
      seenKeys.add(key);
    }
  }

  // 4. Write back if changed.
  const changed =
    finalOrder.length !== stored.length ||
    finalOrder.some(
      (e, i) =>
        !stored[i] || stored[i].kind !== e.kind || stored[i].id !== e.id
    );
  if (changed) {
    await updateDoc(ref, {
      queueOrder: finalOrder,
      queueOrderUpdatedAt: serverTimestamp(),
    });
  }

  // 5. Hydrate: attach the underlying doc to each entry.
  const eventsById = Object.fromEntries(events.map((e) => [e.id, e]));
  const jobsById = Object.fromEntries(directJobs.map((j) => [j.id, j]));
  const items = finalOrder
    .map((entry) => ({
      ...entry,
      data:
        entry.kind === "event"
          ? eventsById[entry.id]
          : jobsById[entry.id] || null,
    }))
    .filter((i) => i.data);

  return { driver, items, queueOrder: finalOrder, events, directJobs };
};

/**
 * Write a new queueOrder array (from a drag-reorder). Doesn't validate —
 * caller is expected to pass the array as just reordered. Reconciliation
 * on the next read will tidy up if anything stale slipped in.
 */
export const updateDriverQueueOrder = async (driverId, queueOrder) => {
  if (!driverId) throw new Error("driverId is required");
  if (!Array.isArray(queueOrder)) throw new Error("queueOrder must be an array");
  const ref = doc(db, DRIVERS_COLLECTION, driverId);
  // Strip any extra fields (data, etc) — only kind + id are stored.
  const slim = queueOrder.map((e) => ({ kind: e.kind, id: e.id }));
  await updateDoc(ref, {
    queueOrder: slim,
    queueOrderUpdatedAt: serverTimestamp(),
  });
};
